import { useState } from "react";
import { toast } from "sonner";
import { sendContact } from "@/lib/api";
import { EnvelopeSimple, MapPin, GlobeHemisphereWest } from "@phosphor-icons/react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Message received. We'll respond personally.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("We couldn't send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 lg:py-28 border-b border-ink/10">
          <p className="overline">Contact</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-ink max-w-4xl">
            Write to us. We read everything.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            Whether you’re an inventor, a researcher, a community leader, or
            simply curious — we welcome your note.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-10">
          <div className="flex items-start gap-4">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-ink text-gold"><EnvelopeSimple size={20} weight="duotone" /></span>
            <div>
              <p className="overline">Email</p>
              <p className="mt-2 text-ink text-lg">hello@winworld.innovation</p>
              <p className="text-ink/50 text-sm mt-1">We respond within 3 working days.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-ink text-gold"><GlobeHemisphereWest size={20} weight="duotone" /></span>
            <div>
              <p className="overline">Global Circle</p>
              <p className="mt-2 text-ink text-lg">Partners in 37 countries</p>
              <p className="text-ink/50 text-sm mt-1">Coordinated remotely, with periodic global convenings.</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-ink text-gold"><MapPin size={20} weight="duotone" /></span>
            <div>
              <p className="overline">Mailing Address</p>
              <p className="mt-2 text-ink text-lg">WinWorld Innovation, Global Office</p>
              <p className="text-ink/50 text-sm mt-1">Postal address available upon request.</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 bg-white rounded-3xl ring-1 ring-ink/10 card-shadow p-8 lg:p-10 space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs tracking-widest uppercase text-ink/60 mb-2">Name</label>
              <input required value={form.name} onChange={set("name")} data-testid="contact-name"
                className="w-full bg-canvas rounded-xl px-4 py-3 outline-none ring-1 ring-ink/10 focus:ring-ink text-ink"/>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-ink/60 mb-2">Email</label>
              <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email"
                className="w-full bg-canvas rounded-xl px-4 py-3 outline-none ring-1 ring-ink/10 focus:ring-ink text-ink"/>
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-ink/60 mb-2">Subject</label>
            <input value={form.subject} onChange={set("subject")} data-testid="contact-subject"
              className="w-full bg-canvas rounded-xl px-4 py-3 outline-none ring-1 ring-ink/10 focus:ring-ink text-ink"/>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-ink/60 mb-2">Message</label>
            <textarea required rows={6} value={form.message} onChange={set("message")} data-testid="contact-message"
              className="w-full bg-canvas rounded-xl px-4 py-3 outline-none ring-1 ring-ink/10 focus:ring-ink text-ink resize-y"/>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-ink rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send message"}
          </button>
        </form>
      </section>
    </div>
  );
}
